// CTV-638 — the mid-wizard business decision.
//
// An anonymous buyer types a business name at step 1, then signs in (step-4
// OTP or the header). If the account they land in already has advertiser seats
// and none of them carries the name they typed, we have to ask which business
// this campaign belongs to instead of silently adopting one of their existing
// advertisers.
//
// The types + helpers live here rather than in `useAuth.tsx` so
// `BusinessDecisionModal` (which `AuthProvider` renders) can import them
// without a runtime import cycle back through the provider.

import { apiClient } from '../../../lib/apiClient';
import type { AdvertiserMembership } from './useAuth';

/**
 * What the buyer has typed into step 1 right now. The wizard body registers
 * this with `AuthProvider` on every keystroke, so the provider always has the
 * live values even though the sign-in it's reacting to destroys and rebuilds
 * that body (the wizard is keyed by identity — see SelfServiceWizard).
 */
export interface WizardBusinessIntent {
  slug: string;
  businessName: string;
  businessWebsite: string;
  contactName: string;
  contactPhone: string;
}

/**
 * Drives BusinessDecisionModal. Non-null only while a decision is open.
 *
 * A snapshot, not a live view: the wizard body remounts underneath the modal
 * (the sign-in flips its identity key), which briefly clears the registered
 * intent. Capturing the values the decision is about keeps the modal's copy —
 * and the contact fields it writes on either branch — stable across that.
 */
export interface PendingBusinessDecision {
  slug: string;
  /** The signed-in address, so the copy can say which account they landed in. */
  email: string;
  businessName: string;
  businessWebsite: string;
  contactName: string;
  contactPhone: string;
  /** The buyer's existing seats — the "use one of these instead" options. */
  memberships: AdvertiserMembership[];
}

export type EnsureSeatOutcome =
  | { status: 'ready'; advertiserId: string }
  // "Go back and edit the business name" — the typo escape hatch. The wizard
  // returns the buyer to step 1 with what they typed intact and re-asks on the
  // next Continue (which may then hit the exact-match branch and never open
  // the modal at all).
  | { status: 'deferred' };

/** Trim + case-fold, the comparison both the client and `ensure-self` use. */
export function normalizeBusinessName(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/**
 * Persist the wizard's contact fields onto the caller's own profile row.
 *
 * `selfservice_advertiser_profiles` is keyed by `user_id`, so contact name and
 * phone are per-user, not per-advertiser. CTV-638's product decision: whatever
 * the buyer typed in the wizard wins and overwrites the saved profile, on BOTH
 * decision branches (create-new and use-existing). They can fix it in Settings.
 *
 * `PUT /profiles/me` is a whole-resource upsert, not a patch — a missing key
 * NULLs the stored column (see profiles.service.ts → trimToNull). Always send
 * both fields.
 *
 * Best-effort: a failed contact save must not strand a buyer who has already
 * chosen their business.
 */
export async function saveWizardContact(intent: {
  contactName: string;
  contactPhone: string;
}): Promise<void> {
  try {
    await apiClient.put('/profiles/me', {
      contactName: intent.contactName.trim(),
      contactPhone: intent.contactPhone.trim(),
    });
  } catch (e) {
    console.warn('[businessDecision] contact save failed:', e);
  }
}
